import type { ReactElement, ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { cn } from '../../ui/utils';

type Side = 'top' | 'right' | 'bottom' | 'left';

/**
 * Names an icon on hover and on keyboard focus. Wrap an icon-only button,
 * toggle or link: it receives `aria-label={label}` unless it already carries
 * its own. For an icon that only informs (a metric's glyph), use `HintedIcon`.
 */
export function IconHint(props: { label: string; children: ReactElement; side?: Side }): ReactNode {
  return (
    <Tooltip>
      <TooltipTrigger asChild aria-label={props.label}>
        {props.children}
      </TooltipTrigger>
      <TooltipContent side={props.side}>{props.label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * A non-interactive icon made focusable so its tooltip reaches keyboard users,
 * announced as an image named `label`. `relative z-10` lifts it above a card's
 * stretched link, which would otherwise swallow the hover.
 */
export function HintedIcon(props: {
  label: string;
  children: ReactNode;
  side?: Side;
  className?: string;
}): ReactNode {
  return (
    <IconHint label={props.label} side={props.side}>
      <span
        role="img"
        // WCAG 1.4.13: a tooltip a pointer can reach must be reachable by keyboard too.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        className={cn(
          'focus-visible:ring-ring relative z-10 inline-flex shrink-0 items-center rounded-sm outline-none focus-visible:ring-2',
          props.className,
        )}
      >
        {props.children}
      </span>
    </IconHint>
  );
}
